import { useState } from 'react';

import Image from 'next/image';

import { Box, Button, ButtonGroup, TextButton } from '@zillow/constellation-10';
import { IconArrowLeftOutline, IconArrowRightOutline } from '@zillow/constellation-icons';

import { useRentalManagerUserClaimWebApiHandler_propertyLookupAll } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import stringHelper from '../../../srcLegacy/shared/utils/stringHelper';

import WizardBodyTemplate from '../WizardBodyTemplate';
import ViewClaimListingsModal from '../modals/ViewClaimListingsModal';

import { STEP } from './constants';

import PersonProfile from '../../assets/PersonProfile.svg';
import HomeSearch from '../../assets/HomeSearch.svg';

import type { WizardBodyTemplateProps } from '../WizardStepController';

const { pluralize } = stringHelper;

const WizVerifyContactSuccess = ({
  onGoToNextStep,
  onGoToPrevStep,
  onGoToStep,
  progressStepper,
}: WizardBodyTemplateProps) => {
  const [showViewListingsModal, setShowViewListingsModal] = useState(false);

  const {
    data,
    isFetching: isTransitioning,
    isError: hasError,
  } = useRentalManagerUserClaimWebApiHandler_propertyLookupAll({
    includeVerified: true,
    includeUnverified: false,
  });

  const listingCount = data?.totalListingCount ?? null;
  const contactMethodsCount = data?.totalContactMethodCount ?? 0;

  const handleViewListings = () => {
    setShowViewListingsModal(true);
  };

  const handleFindListings = () => {
    onGoToStep(STEP.FIND_LISTING_RESULTS);
  };

  const renderSubtitle = () =>
    listingCount != null ? (
      <>
        {`Congratulations! You have verified ${pluralize(contactMethodsCount, 'contact', 'contacts')} and we’ve 
            linked ${pluralize(listingCount, 'listing', 'listings')} to your account. `}
        <TextButton onClick={handleViewListings} css={{ fontWeight: '600' }}>
          View your listings
        </TextButton>
      </>
    ) : (
      <>
        {`No listings are associated with the contact you verified.
             To transfer listings, search by other contact methods.`}
      </>
    );

  const renderDescription = () =>
    listingCount != null ? (
      <>
        We’ll continue searching for listings with this contact information and alert you with new verified listings in
        your Zillow Rental Manager dashboard.
      </>
    ) : null;

  const renderIcon = () => (
    <Image
      src={listingCount != null ? PersonProfile : HomeSearch}
      alt={listingCount != null ? 'A business person' : 'Searching for listings'}
      fill
    />
  );

  return (
    <WizardBodyTemplate
      title={listingCount != null ? `You’ve claimed ${pluralize(listingCount, 'listing', 'listings')}` : ''}
      subtitle={renderSubtitle()}
      description={renderDescription()}
      isTransitioning={isTransitioning}
      hasError={hasError}
      icon={renderIcon()}
      progressStepper={progressStepper}
      footer={
        <ButtonGroup
          css={{ marginTop: 'auto', paddingTop: '600', justifyContent: 'center' }}
          aria-label="Find listing success"
        >
          <Button
            tone="brand"
            emphasis="outlined"
            onClick={onGoToPrevStep ?? undefined}
            disabled={!onGoToPrevStep}
            icon={<IconArrowLeftOutline />}
          >
            Back
          </Button>
          <Button
            tone="brand"
            emphasis="filled"
            onClick={onGoToNextStep ?? undefined}
            disabled={Boolean(listingCount != null && listingCount < 1)}
            icon={<IconArrowRightOutline />}
          >
            Next
          </Button>
        </ButtonGroup>
      }
    >
      {listingCount == null && (
        <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Button tone="brand" emphasis="filled" css={{ marginTop: '200' }} onClick={handleFindListings}>
            Find listings
          </Button>
        </Box>
      )}
      {showViewListingsModal && (
        <ViewClaimListingsModal
          isOpen={showViewListingsModal}
          onClose={() => setShowViewListingsModal(false)}
          includeUnverified={false}
        />
      )}
    </WizardBodyTemplate>
  );
};

export default WizVerifyContactSuccess;
